import { useCallback, useMemo, useState } from 'react';
import { SurveyAnswer } from '../types/cancellation';
import { SURVEY_QUESTIONS } from '../constants/surveyQuestions';
import { getUUID } from '@/lib';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { ANALYTICS_EVENTS, ANALYTICS_STEP_NAME, INITIAL_QUESTION_ID } from '../constants/cancellation';

export const useCancellationSurvey = () => {
    const [currentQuestionId, setCurrentQuestionId] = useState(INITIAL_QUESTION_ID);
    const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
    const [canSkip, setCanSkip] = useState(true);
    const [responseId] = useState(() => getUUID());
    const [questionHistory, setQuestionHistory] = useState<string[]>([INITIAL_QUESTION_ID]);

    // Get current question
    const currentQuestion = useMemo(() => {
        return SURVEY_QUESTIONS.find(q => q.questionId === currentQuestionId);
    }, [currentQuestionId]);

    // Check if current answer is valid
    const isCurrentAnswerValid = useMemo(() => {
        const answer = answers[currentQuestionId];
        if (!currentQuestion?.required) return true;

        if (currentQuestion.type === 'text') {
            return typeof answer === 'string' && answer.trim().length > 0;
        }

        if (currentQuestion.type === 'multiSelect') {
            return Array.isArray(answer) && answer.length > 0;
        }

        if (currentQuestion.type === 'select') {
            return typeof answer === 'string' && answer.length > 0;
        }

        return false;
    }, [answers, currentQuestionId, currentQuestion]);

    // Handle answer change
    const handleAnswer = useCallback(
        (answer: string | string[]) => {
            setAnswers(prev => ({
                ...prev,
                [currentQuestionId]: answer,
            }));

            // Disable skip after first answer
            if (canSkip) {
                setCanSkip(false);
            }
        },
        [currentQuestionId, canSkip]
    );

    // Navigate to next question
    const goToNextQuestion = useCallback(
        (answerValue?: string | string[]) => {
            if (!currentQuestion) return false;

            let nextQuestionId: string | undefined;

            // For select type, check if the selected option has a nextQuestionId
            if (currentQuestion.type === 'select' && typeof answerValue === 'string') {
                const selectedOption = currentQuestion.options?.find(opt => opt.dbMap === answerValue);
                nextQuestionId = selectedOption?.nextQuestionId;
            }

            // If no option-specific next question, use the question's nextQuestionId
            if (!nextQuestionId) {
                nextQuestionId = currentQuestion.nextQuestionId;
            }

            if (nextQuestionId) {
                setCurrentQuestionId(nextQuestionId);
                setQuestionHistory(prev => [...prev, nextQuestionId]);
                return true;
            }

            // No more questions
            return false;
        },
        [currentQuestion]
    );

    // Navigate to previous question
    const goToPreviousQuestion = useCallback(() => {
        if (questionHistory.length <= 1) return false;

        const newHistory = [...questionHistory];
        newHistory.pop(); // Remove current
        const previousQuestionId = newHistory[newHistory.length - 1];

        if (previousQuestionId) {
            // Clear all answers for questions that came after the previous question
            // This ensures when you go back and change an answer, subsequent questions reset
            setAnswers(prev => {
                const newAnswers = { ...prev };
                // Remove answers for questions not in the new history
                Object.keys(newAnswers).forEach(questionId => {
                    if (!newHistory.includes(questionId)) {
                        delete newAnswers[questionId];
                    }
                });
                return newAnswers;
            });

            setQuestionHistory(newHistory);
            setCurrentQuestionId(previousQuestionId);
        }
        return true;
    }, [questionHistory]);

    // Check if this is the first question
    const isFirstQuestion = questionHistory.length === 1;

    // Check if survey is complete
    const isSurveyComplete = !currentQuestion?.nextQuestionId && isCurrentAnswerValid;

    // Track survey start
    const trackStart = useCallback(() => {
        stanAnalytics(ANALYTICS_EVENTS.SURVEY_STARTED, {
            props: {
                step_name: ANALYTICS_STEP_NAME,
                response_id: responseId,
            },
        });
    }, [responseId]);

    // Track survey completion
    const trackCompleted = useCallback(() => {
        stanAnalytics(ANALYTICS_EVENTS.SURVEY_COMPLETED, {
            props: {
                step_name: ANALYTICS_STEP_NAME,
                response_id: responseId,
            },
        });
    }, [responseId]);

    // Track survey skip
    const trackSkipped = useCallback(() => {
        stanAnalytics(ANALYTICS_EVENTS.SURVEY_SKIPPED, {
            props: {
                step_name: ANALYTICS_STEP_NAME,
                response_id: responseId,
            },
        });
    }, [responseId]);

    // Submit survey
    const submitSurvey = useCallback(async () => {
        // TODO: Implement API call to submit survey
        trackCompleted();
    }, [trackCompleted]);

    // Get all answers in the format expected by API
    const getSurveyAnswers = useCallback((): SurveyAnswer[] => {
        return Object.entries(answers).map(([questionId, answer]) => ({
            questionId,
            answer,
        }));
    }, [answers]);

    // Reset survey to initial state
    const resetSurvey = useCallback(() => {
        setCurrentQuestionId(INITIAL_QUESTION_ID);
        setAnswers({});
        setCanSkip(true);
        setQuestionHistory([INITIAL_QUESTION_ID]);
    }, []);

    return {
        currentQuestion,
        currentQuestionId,
        answers,
        canSkip,
        responseId,
        isCurrentAnswerValid,
        isFirstQuestion,
        isSurveyComplete,
        handleAnswer,
        goToNextQuestion,
        goToPreviousQuestion,
        submitSurvey,
        getSurveyAnswers,
        trackStart,
        trackCompleted,
        trackSkipped,
        resetSurvey,
    };
};
